import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, CheckCircle2, Camera, Signal, Sun, BatteryCharging,
  Moon, Radio, MapPin, ShieldCheck, Wrench, Scale, HardDrive, Eye,
} from "lucide-react";
import { Eyebrow, SectionHeading } from "@/components/Section";
import { SEO } from "@/components/SEO";

const SITE_URL = "https://it-servicepoint.eu";

const SPECS = [
  { icon: Camera, title: "4K Ultra HD (8 MP)", desc: "3840 × 2160 Pixel – Kennzeichen, Gesichter und Details bleiben auch beim Heranzoomen erkennbar." },
  { icon: Signal, title: "4G-LTE statt WLAN", desc: "SIM-Karte rein, fertig. Die Kamera funkt über das Mobilfunknetz – ganz ohne Internetanschluss vor Ort." },
  { icon: Eye, title: "355° Schwenk / 140° Neigung", desc: "Ein Gerät überwacht, wofür sonst mehrere Kameras nötig wären – inkl. bis zu 32 Preset-Positionen." },
  { icon: BatteryCharging, title: "Akku + Solar", desc: "21,6-Wh-Akku, optional mit Solarpanel dauerhaft autark – keine Stromleitung, kein Grabenbau." },
  { icon: Moon, title: "Farb-Nachtsicht", desc: "Spotlights liefern nachts Farbbilder; Infrarot-Nachtsicht bis 10 m Reichweite inklusive." },
  { icon: Radio, title: "Smarte Erkennung + PIR", desc: "Personen- und Fahrzeugerkennung, PIR-Sensor mit 120°-Erfassung – Push-Alarm aufs Handy in Sekunden." },
  { icon: HardDrive, title: "Lokale Speicherung", desc: "Aufnahmen auf microSD-Karte (bis 128 GB) direkt in der Kamera – DSGVO-freundlich ohne Cloud-Zwang." },
  { icon: ShieldCheck, title: "IP64 wetterfest", desc: "Regen, Staub, Frost – gebaut für den Dauereinsatz im Außenbereich, Zwei-Wege-Audio inklusive." },
];

const SCENARIOS = [
  { title: "Baustellen", desc: "Materialdiebstahl und Vandalismus kosten Bauunternehmen jedes Jahr Millionen. Eine 4G-Kamera überwacht die Baustelle ab dem ersten Tag – noch bevor Strom und Internet liegen." },
  { title: "Landwirtschaft & Stallungen", desc: "Felder, Maschinenhallen, Weiden und Ställe liegen selten in WLAN-Reichweite. Mit Solarpanel läuft die Überwachung das ganze Jahr autark." },
  { title: "Ferienhäuser & Grundstücke", desc: "Das Wochenendhaus, der Schrebergarten, das unbebaute Grundstück – per App schauen Sie jederzeit nach dem Rechten, egal wo Sie gerade sind." },
  { title: "PV-Anlagen & Freiflächen", desc: "Photovoltaik-Freiflächenanlagen sind beliebte Ziele für Kabeldiebe. Kameras mit Personenerkennung melden Eindringlinge, bevor Schaden entsteht." },
  { title: "Firmengelände & Außenlager", desc: "Container, Fuhrpark, Lagerflächen hinter der Halle – überall dort, wo die bestehende IT-Infrastruktur nicht hinreicht." },
  { title: "Events & Zwischenlösungen", desc: "Temporäre Überwachung für Veranstaltungen, Leerstände oder Umbauphasen – aufbauen, SIM aktivieren, läuft. Und danach einfach wieder mitnehmen." },
];

const PORTFOLIO = [
  { title: "4G-LTE-Kameras", sub: "Go-Serie", desc: "Für Orte ohne WLAN und Strom: akku- oder solarbetrieben, Mobilfunk-Anbindung, ideal für abgelegene Objekte.", badge: "Netzunabhängig" },
  { title: "WLAN-Kameras", sub: "E- & Argus-Serie", desc: "Schnell installiert im Haus, Büro oder Hof – ohne Kabelverlegung, mit Akku- oder Netzbetrieb.", badge: "Flexibel" },
  { title: "PoE-Kameras & NVR", sub: "RLK-Systeme", desc: "Die Profi-Lösung für Firmengelände: kabelgebunden, 24/7-Aufzeichnung auf eigenem Rekorder – ohne Cloud.", badge: "Dauerbetrieb" },
  { title: "Solar & Zubehör", sub: "Panels, Halterungen", desc: "Solarpanels, Masten und Halterungen für den dauerhaft autarken Betrieb Ihrer Kameras.", badge: "Autark" },
];

const SERVICES = [
  { icon: Wrench, title: "Beratung & Planung", desc: "Wir analysieren Ihr Objekt, wählen die passenden Kameras und planen Positionen, Blickwinkel und Funkabdeckung." },
  { icon: Signal, title: "SIM & Tarif", desc: "Wir empfehlen den passenden IoT-/Datentarif für Ihren Standort und richten die Mobilfunk-Anbindung komplett ein." },
  { icon: Camera, title: "Montage & Einrichtung", desc: "Installation, App-Konfiguration, Erkennungszonen, Push-Alarme, Preset-Positionen – schlüsselfertig übergeben." },
  { icon: ShieldCheck, title: "Sichere Integration", desc: "Kameras kommen bei uns ins getrennte VLAN – sauber isoliert vom Firmennetz, wie es sich sicherheitstechnisch gehört." },
  { icon: Scale, title: "DSGVO-konform", desc: "Beschilderung, Speicherfristen, Erfassungsbereiche, Mitarbeiterinformation – wir richten Ihre Videoüberwachung rechtssicher ein." },
  { icon: Sun, title: "Wartung & Support", desc: "Firmware-Updates, Akku-/Solar-Check und ein Ansprechpartner, wenn mal etwas nicht läuft – auf Wunsch als Managed Service." },
];

const FAQ = [
  { q: "Funktioniert die Kamera wirklich ganz ohne WLAN und Strom?", a: "Ja. 4G-Kameras wie die Reolink Go PT Ultra arbeiten mit einer SIM-Karte über das Mobilfunknetz und werden per Akku betrieben. Mit einem Solarpanel lädt sich der Akku selbst nach – die Kamera läuft dauerhaft autark. Voraussetzung ist lediglich ausreichender Mobilfunkempfang am Standort, den wir vorab für Sie prüfen." },
  { q: "Was kostet der Mobilfunktarif für eine 4G-Kamera?", a: "Für typische Überwachungsszenarien reichen schlanke IoT-/Datentarife ab wenigen Euro im Monat. Der tatsächliche Datenverbrauch hängt von Auflösung, Aufnahmeverhalten und Live-Zugriffen ab – wir kalkulieren das für Ihren Einsatzfall und empfehlen einen passenden Tarif." },
  { q: "Ist Videoüberwachung auf meinem Grundstück überhaupt erlaubt?", a: "Auf dem eigenen Grundstück grundsätzlich ja – aber es gibt Regeln: Öffentliche Wege und Nachbargrundstücke dürfen nicht miterfasst werden, es braucht Hinweisschilder, definierte Speicherfristen und bei Mitarbeitenden eine transparente Information. Wir richten Ihre Anlage DSGVO-konform ein und dokumentieren das für Sie." },
  { q: "Brauche ich ein Cloud-Abo für die Aufnahmen?", a: "Nein. Reolink-Kameras speichern lokal auf microSD-Karte (bis 128 GB) oder – bei PoE-Systemen – auf einem eigenen Rekorder (NVR). Ihre Aufnahmen bleiben bei Ihnen, ohne laufende Cloud-Kosten und ohne Datentransfer an Dritte." },
  { q: "Kann ich mit einer Kamera einen größeren Bereich abdecken?", a: "Mit einer Schwenk-Neige-Kamera wie der Go PT Ultra ja: 355° Schwenkbereich, 140° Neigung und bis zu 32 gespeicherte Preset-Positionen decken Flächen ab, für die sonst mehrere starre Kameras nötig wären." },
  { q: "Übernehmt Ihr auch die komplette Installation?", a: "Ja – von der Standortanalyse über Montage, SIM-Einrichtung und App-Konfiguration bis zur DSGVO-Dokumentation. Auf Wunsch inklusive laufender Wartung als Managed Service, damit die Anlage dauerhaft zuverlässig läuft." },
];

export default function Ueberwachungskameras() {
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      "@id": `${SITE_URL}/ueberwachungskameras#service`,
      name: "Reolink Überwachungskameras & Videoüberwachung",
      alternateName: ["Videoüberwachung", "4G Überwachungskamera", "Kamera-Installation"],
      provider: { "@id": `${SITE_URL}/#organization` },
      serviceType: "Video surveillance consulting and installation",
      description:
        "Beratung, Installation und Wartung von Reolink Überwachungskameras – 4G-LTE-Kameras für netzunabhängige Gebiete (Go PT Ultra), WLAN- und PoE-Systeme. DSGVO-konform, mit Solar-Option, für Baustellen, Landwirtschaft und Firmengelände in Hessen.",
      areaServed: { "@type": "AdministrativeArea", name: "Hessen, Thüringen, Unterfranken" },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Start", item: SITE_URL },
        { "@type": "ListItem", position: 2, name: "Überwachungskameras", item: `${SITE_URL}/ueberwachungskameras` },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQ.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ];

  return (
    <div data-testid="cameras-page" className="relative">
      <SEO
        title="Reolink Überwachungskameras · 4G-Videoüberwachung ohne WLAN & Strom"
        description="Reolink Überwachungskameras vom IT-Profi: 4G-LTE-Kameras (Go PT Ultra) für Baustellen, Landwirtschaft und abgelegene Grundstücke – ohne WLAN, ohne Stromanschluss. Beratung, Installation, DSGVO-konforme Einrichtung in Hessen."
        keywords="Reolink Überwachungskamera, 4G Überwachungskamera, Go PT Ultra, Videoüberwachung ohne WLAN, Überwachungskamera ohne Strom, Baustellenüberwachung, Kamera Solar, Videoüberwachung DSGVO, Überwachungskamera Fulda, Videoüberwachung Hessen, PoE Kamera NVR"
        jsonLd={jsonLd}
        type="service"
      />

      {/* HERO */}
      <section className="relative overflow-hidden bg-ink text-white border-b border-line">
        <div className="absolute inset-0">
          <img
            src="/wp-images/ueberwachungskamera-4g.jpg"
            alt="Reolink 4G-Überwachungskamera an einem Mast in netzunabhängigem Gebiet"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-ink/95 via-ink/75 to-ink/30" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 sm:px-12 py-24 sm:py-36">
          <div className="max-w-3xl">
            <Eyebrow className="text-accent">Videoüberwachung · Reolink Partner-Produkte</Eyebrow>
            <h1 className="mt-6 font-display font-black text-4xl sm:text-6xl lg:text-7xl tracking-tightest leading-[0.95]">
              Überwachung, wo es<br />
              <span className="text-accent">kein WLAN gibt.</span>
            </h1>
            <p className="mt-8 max-w-2xl text-lg sm:text-xl text-white/85 leading-relaxed">
              Reolink Überwachungskameras mit <strong className="text-white">4G-LTE, Akku und Solar</strong> sichern Baustellen, Höfe, Grundstücke und Außenlager – ganz ohne Internetanschluss oder Stromleitung. Wir beraten, installieren und richten alles DSGVO-konform ein.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link
                to="/kontakt"
                data-testid="cameras-hero-cta"
                className="group inline-flex items-center justify-center gap-2 px-8 py-4 bg-accent text-ink font-medium hover:bg-accent/90 transition-colors"
              >
                Kostenfreie Beratung anfragen
                <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
              </Link>
              <a
                href="tel:+4966299153890"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-white/40 text-white hover:bg-white/10 transition-colors font-medium"
              >
                +49 (0) 6629 9153890
              </a>
            </div>
            <div className="mt-12 flex flex-wrap gap-3">
              {["Kein WLAN nötig", "Kein Stromanschluss nötig", "Solar-Option", "DSGVO-konform", "Lokale Speicherung"].map((c) => (
                <span key={c} className="border border-white/20 bg-white/5 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-white/85">
                  {c}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* GO PT ULTRA HIGHLIGHT */}
      <section className="bg-white border-b border-line">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 py-24 sm:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-16">
            <div className="lg:col-span-8">
              <Eyebrow>Produkt-Highlight</Eyebrow>
              <h2 className="mt-4 font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tightest leading-[1.02] text-ink">
                Reolink Go PT Ultra.<br />
                <span className="text-primary">4G-Sicherheit für netzunabhängige Gebiete.</span>
              </h2>
            </div>
            <div className="lg:col-span-4">
              <p className="text-muted text-lg leading-relaxed">
                Drahtloser Schutz auf Profi-Niveau: 4K-Auflösung, Schwenk-Neige-Mechanik und Mobilfunk-Anbindung – die Kamera für Orte, an denen sonst nichts funkt.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {SPECS.map((s, i) => (
              <div key={s.title} className="border border-line bg-white p-7 lift" data-testid={`cameras-spec-${i}`}>
                <div className="w-11 h-11 bg-soft border border-line flex items-center justify-center text-primary mb-5">
                  <s.icon size={20} />
                </div>
                <h3 className="font-display font-bold text-lg text-ink leading-tight">{s.title}</h3>
                <p className="mt-3 text-muted text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-12 border border-line bg-soft p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-primary">So einfach geht's</div>
              <div className="mt-2 font-display font-bold text-xl text-ink">SIM einlegen → montieren → App verbinden. Fertig.</div>
            </div>
            <Link
              to="/kontakt"
              data-testid="cameras-gopt-cta"
              className="group inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-primary text-white font-medium hover:bg-primary-hover transition-colors shrink-0"
            >
              Angebot anfragen
              <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>

      {/* SCENARIOS with image */}
      <section className="bg-soft border-b border-line">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 py-24 sm:py-32">
          <SectionHeading
            eyebrow="Einsatzszenarien"
            title="Überall dort, wo Ihre IT nicht hinreicht."
            sub="4G-Kameras schließen die Lücke zwischen „müsste man mal überwachen“ und „da liegt doch kein Kabel“. Sechs typische Einsatzfälle aus unserer Praxis."
          />
          <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <div className="relative overflow-hidden border border-line h-full min-h-[320px]">
                <img
                  src="/wp-images/ueberwachungskamera-baustelle.jpg"
                  alt="Baustellenüberwachung mit solarbetriebener 4G-Überwachungskamera"
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute bottom-4 left-4 bg-ink/85 text-white px-4 py-2 font-mono text-[10px] uppercase tracking-[0.25em]">
                  Baustelle · autark ab Tag 1
                </div>
              </div>
            </div>
            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-6">
              {SCENARIOS.map((s, i) => (
                <article key={s.title} className="border border-line bg-white p-6 lift" data-testid={`cameras-scenario-${i}`}>
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-accent">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="mt-3 font-display font-bold text-lg text-ink">{s.title}</h3>
                  <p className="mt-3 text-muted text-sm leading-relaxed">{s.desc}</p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PORTFOLIO */}
      <section className="bg-white border-b border-line">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 py-24 sm:py-32">
          <SectionHeading
            eyebrow="Das Reolink-Portfolio"
            title="Die passende Kamera für jedes Objekt."
            sub="4G ist nicht immer die richtige Antwort. Wir wählen aus dem gesamten Reolink-Sortiment die Technik, die zu Ihrem Standort passt – herstellerneutral beraten, sauber installiert."
          />
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {PORTFOLIO.map((p, i) => (
              <div key={p.title} className="border border-line bg-white p-8 flex flex-col lift" data-testid={`cameras-portfolio-${i}`}>
                <span className="self-start bg-soft border border-line px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-primary">{p.badge}</span>
                <h3 className="mt-5 font-display font-bold text-xl text-ink">{p.title}</h3>
                <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-muted">{p.sub}</div>
                <p className="mt-4 text-muted text-sm leading-relaxed flex-1">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OUR SERVICE */}
      <section className="bg-soft border-b border-line">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 py-24 sm:py-32">
          <SectionHeading
            eyebrow="Warum mit IT-Servicepoint?"
            title="Kamera kaufen kann jeder. Sicher betreiben nicht."
            sub="Eine Überwachungskamera ist ein Netzwerkgerät – und ein Datenschutz-Thema. Wir liefern beides mit: saubere IT-Integration und rechtssichere Einrichtung."
          />
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((s, i) => (
              <div key={s.title} className="border border-line bg-white p-8 lift" data-testid={`cameras-service-${i}`}>
                <div className="w-11 h-11 bg-soft border border-line flex items-center justify-center text-primary mb-5">
                  <s.icon size={20} />
                </div>
                <h3 className="font-display font-bold text-lg text-ink">{s.title}</h3>
                <p className="mt-3 text-muted text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DSGVO NOTE */}
      <section className="bg-ink text-white border-b border-line relative overflow-hidden">
        <div className="absolute inset-0 opacity-25 pointer-events-none" style={{
          background: "radial-gradient(ellipse at 15% 20%, rgba(29,169,218,0.35), transparent 55%), radial-gradient(ellipse at 85% 80%, rgba(164,197,57,0.25), transparent 55%)",
        }} />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-12 py-20 sm:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-accent">
              <Scale size={14} /> Videoüberwachung & DSGVO
            </div>
            <h2 className="mt-5 font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tightest leading-[1.05]">
              Rechtssicher überwachen – ohne Abmahnrisiko.
            </h2>
            <p className="mt-5 text-white/80 text-lg leading-relaxed max-w-2xl">
              Falsch ausgerichtete Kameras, fehlende Hinweisschilder oder zu lange Speicherfristen werden schnell teuer. Wir richten Erfassungsbereiche, Beschilderung, Speicherfristen und Mitarbeiterinformation korrekt ein – und dokumentieren alles für Sie.
            </p>
          </div>
          <div className="lg:col-span-4">
            <ul className="space-y-3">
              {["Erfassungsbereiche ohne öffentliche Wege", "Hinweisschilder nach Art. 13 DSGVO", "Definierte Speicher- & Löschfristen", "Lokale Speicherung statt Cloud-Zwang", "Dokumentation für Ihre Unterlagen"].map((p) => (
                <li key={p} className="flex items-start gap-2 text-white/90 text-sm">
                  <CheckCircle2 size={16} className="text-accent shrink-0 mt-0.5" /> {p}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-soft border-b border-line">
        <div className="max-w-4xl mx-auto px-6 sm:px-12 py-24 sm:py-28">
          <Eyebrow>Häufige Fragen</Eyebrow>
          <h2 className="mt-4 font-display font-black text-3xl sm:text-5xl tracking-tightest text-ink">FAQ.</h2>
          <div className="mt-12 space-y-4">
            {FAQ.map((f, i) => (
              <details key={i} className="border border-line bg-white p-6 group" data-testid={`cameras-faq-${i}`}>
                <summary className="font-display font-bold text-lg text-ink cursor-pointer flex items-center justify-between gap-4">
                  {f.q}
                  <span className="text-primary group-open:rotate-45 transition-transform text-xl">+</span>
                </summary>
                <p className="mt-4 text-muted leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-primary text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-20 pointer-events-none" style={{
          backgroundImage: "radial-gradient(rgba(255,255,255,0.15) 1px, transparent 1px)",
          backgroundSize: "24px 24px",
        }} />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-12 py-20 sm:py-28 text-center">
          <MapPin size={40} className="text-accent mx-auto" />
          <h2 className="mt-6 font-display font-black text-3xl sm:text-5xl tracking-tightest leading-[1.05] max-w-3xl mx-auto">
            Welches Objekt sollen wir für Sie absichern?
          </h2>
          <p className="mt-6 max-w-2xl mx-auto text-white/80 text-lg">
            30 Minuten Erstgespräch · kostenfrei · unverbindlich. Wir prüfen Mobilfunkempfang, planen Kamerapositionen und nennen Ihnen einen Festpreis.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/kontakt"
              data-testid="cameras-cta-contact"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-accent text-ink font-medium hover:bg-accent/90 transition-colors"
            >
              Beratungsgespräch anfragen <ArrowRight size={18} />
            </Link>
            <a
              href="tel:+4966299153890"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-white/40 text-white hover:bg-white/10 transition-colors"
            >
              +49 (0) 6629 9153890
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
